import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {Box, Tooltip} from '@primer/react'
import {memo, useCallback, useRef} from 'react'
import type {SpaceProps, TypographyProps} from 'styled-system'

import {AutosizeTextInput} from '../../../components/common/autosize-text-input'
import {BorderlessTextInput} from '../../../components/common/borderless-text-input'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {useEmojiAutocomplete} from '../../../hooks/common/use-emoji-autocomplete'
import type {ChartState} from '../../../state-providers/charts/use-charts'
import {InsightsResources} from '../../../strings'
import {useInsightsChartName} from '../hooks/use-insights-chart-name'

interface InsightsChartNameProps {
  chart: ChartState
}

const ConditionalTooltip = ({
  showTooltip,
  text = '',
  children,
}: {
  showTooltip: boolean
  text: string
  children: React.ReactNode
}) => {
  return showTooltip ? (
    <Tooltip aria-label={text} direction="s" align="right">
      {children}
    </Tooltip>
  ) : (
    <>children</>
  )
}

export const InsightsChartName: React.FC<TypographyProps & SpaceProps & InsightsChartNameProps> = memo(
  ({chart, ...props}) => {
    const {hasWritePermissions} = ViewerPrivileges()
    const {chartName, setLocalChartName, revertChartName, saveChartName} = useInsightsChartName(chart)
    const inputRef = useRef<HTMLInputElement>(null)

    const onChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalChartName(e.target.value)
      },
      [setLocalChartName],
    )
    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
          case 'Enter':
            saveChartName()
            break

          case 'Escape':
            event.stopPropagation() // Prevents closing the Insights view
            revertChartName()
            break
        }
      },
      [revertChartName, saveChartName],
    )
    const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)

    const onClick = useCallback(() => {
      inputRef.current?.focus()
    }, [])

    const onBlur = useCallback(() => {
      if (document.hasFocus()) {
        saveChartName()
      }
    }, [saveChartName])

    const autocompleteProps = useEmojiAutocomplete()

    return (
      <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
        <ConditionalTooltip showTooltip={hasWritePermissions} text="Rename">
          <Box
            sx={{
              ...(hasWritePermissions
                ? {
                    '&:focus-within': {
                      boxShadow: 'primary.shadow.focus',
                      borderRadius: '2',
                    },
                    '&:hover:not(:focus-within)': {
                      boxShadow: theme => `0 0 0 3px ${theme.colors.border.default}`,
                      borderRadius: '2',
                    },
                  }
                : {}),
              px: 1,
              mx: -1,
            }}
          >
            {hasWritePermissions ? (
              <InlineAutocomplete {...autocompleteProps}>
                <AutosizeTextInput
                  as={BorderlessTextInput}
                  {...props}
                  ref={inputRef}
                  autoComplete="off"
                  placeholder={InsightsResources.addInsightsChartNamePlaceholder}
                  aria-label={InsightsResources.addInsightsChartNamePlaceholder}
                  value={chartName}
                  onChange={onChange}
                  onClick={onClick}
                  onBlur={onBlur}
                  id="insightsChartNameInput"
                  {...inputCompositionProps}
                  {...testIdProps('insights-chart-name')}
                />
              </InlineAutocomplete>
            ) : (
              <Box sx={{fontSize: 3, fontWeight: 600}} {...testIdProps('insights-chart-name')}>
                {chartName}
              </Box>
            )}
          </Box>
        </ConditionalTooltip>
      </Box>
    )
  },
)

InsightsChartName.displayName = 'InsightsChartName'

try{ ConditionalTooltip.displayName ||= 'ConditionalTooltip' } catch {}